import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

const AdminProgressAnalytics = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const [grades, setGrades] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');

  const classes = [
    'Advanced Biology F25',
    'Adv Algebra 2 - Rose',
    'Schwarz: English II',
    'Spanish 1 - Fall 2025',
    'Adv Design Applications'
  ];



  // Fetch grades
  const fetchGrades = async () => {
    try {
      const gradesSnapshot = await getDocs(collection(db, 'grades'));
      const gradesList = gradesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      gradesList.sort((a, b) => new Date(b.date) - new Date(a.date));
      setGrades(gradesList);
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };



  // Delete grade entry
  const handleDeleteGrade = async (gradeId) => {
    if (!window.confirm('Are you sure you want to delete this grade entry?')) return;

    try {
      await deleteDoc(doc(db, 'grades', gradeId));
      fetchGrades();
    } catch (error) {
      console.error('Error deleting grade:', error);
    }
  };

  // Export grades as CSV
  const handleExportCSV = () => {
    const dataToExport = getClassGrades(selectedClass);

    if (dataToExport.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Date', 'Class', 'Formative %', 'Summative %', 'Overall %', 'Notes'];
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(grade => [
        new Date(grade.date).toLocaleDateString(),
        `"${grade.className}"`, // Quote class name in case of commas
        grade.formativePercent || '',
        grade.summativePercent || '',
        grade.overallPercent || '',
        `"${(grade.notes || '').replace(/"/g, '""')}"` // Escape quotes in notes
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `grades_export_${selectedClass === 'all' ? 'all' : selectedClass.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get grades for specific class
  const getClassGrades = (className) => {
    if (className === 'all') return grades;
    return grades.filter(grade => grade.className === className);
  };

  // Calculate class statistics
  const calculateClassStats = (className) => {
    const classGrades = getClassGrades(className);
    if (classGrades.length === 0) return null;

    // Filter out null/undefined values before calculating averages
    const validFormativeGrades = classGrades.filter(grade => grade.formativePercent != null && !isNaN(grade.formativePercent));
    const validSummativeGrades = classGrades.filter(grade => grade.summativePercent != null && !isNaN(grade.summativePercent));
    const validOverallGrades = classGrades.filter(grade => grade.overallPercent != null && !isNaN(grade.overallPercent));

    const formativeAvg = validFormativeGrades.length > 0
      ? validFormativeGrades.reduce((sum, grade) => sum + grade.formativePercent, 0) / validFormativeGrades.length
      : 0;

    const summativeAvg = validSummativeGrades.length > 0
      ? validSummativeGrades.reduce((sum, grade) => sum + grade.summativePercent, 0) / validSummativeGrades.length
      : 0;

    const overallAvg = validOverallGrades.length > 0
      ? validOverallGrades.reduce((sum, grade) => sum + grade.overallPercent, 0) / validOverallGrades.length
      : 0;

    return {
      formativeAvg: formativeAvg.toFixed(1),
      summativeAvg: summativeAvg.toFixed(1),
      overallAvg: overallAvg.toFixed(1),
      totalEntries: classGrades.length,
      validFormativeEntries: validFormativeGrades.length,
      validSummativeEntries: validSummativeGrades.length
    };
  };

  // Generate chart data for a class
  const generateChartData = (className) => {
    const classGrades = getClassGrades(className);
    return classGrades
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(grade => ({
        date: new Date(grade.date).toLocaleDateString(),
        formative: grade.formativePercent,
        summative: grade.summativePercent,
        overall: grade.overallPercent
      }));
  };

  // Simple chart component (text-based for now, can be enhanced with a charting library later)
  const SimpleChart = ({ data, title, color }) => {
    if (data.length === 0) {
      return (
        <div className="text-center py-8 text-gray-400">
          No data available for {title}
        </div>
      );
    }

    // Select metric values and ignore missing entries
    const metricKey = color === 'formative' ? 'formative' : color === 'summative' ? 'summative' : 'overall';
    const metricValues = data
      .map(d => d[metricKey])
      .filter(v => typeof v === 'number' && !isNaN(v));

    if (metricValues.length === 0) {
      return (
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <h3 className="text-lg font-bold mb-4">{title}</h3>
          <div className="text-center py-6 text-gray-500 text-sm">No data entered yet</div>
        </div>
      );
    }

    // Dynamic domain with minimum range
    const rawMin = Math.min(...metricValues);
    const rawMax = Math.max(...metricValues);
    const span = Math.max(0.0001, rawMax - rawMin);
    const paddingAmt = Math.max(2, span * 0.1);
    let minValue = Math.max(0, rawMin - paddingAmt);
    let maxValue = Math.min(100, rawMax + paddingAmt);
    const minRange = 20;
    if (maxValue - minValue < minRange) {
      const needed = minRange - (maxValue - minValue);
      minValue = Math.max(0, minValue - needed / 2);
      maxValue = Math.min(100, maxValue + needed / 2);
    }
    const denom = Math.max(0.0001, maxValue - minValue);

    return (
      <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-1">
            <h3 className="text-lg font-bold mb-4">{title}</h3>
            <div className="space-y-2">
              {data.slice(-10).map((point, index) => {
                const value = point[metricKey];
                const hasValue = typeof value === 'number' && !isNaN(value);
                return (
                  <div key={index} className="flex items-center space-x-4">
                    <span className="text-sm text-gray-400 w-20">{point.date}</span>
                    <div className="flex-1 flex space-x-1">
                      {hasValue ? (
                        <div
                          className={`flex-1 h-4 rounded ${color === 'formative' ? 'bg-blue-600' : color === 'summative' ? 'bg-red-600' : 'bg-green-600'}`}
                          style={{ width: `${((value - minValue) / denom) * 100}%` }}
                        />
                      ) : (
                        <div className="flex-1 h-4 rounded bg-transparent border border-dashed border-[#2a2a2a]" />
                      )}
                    </div>
                    <span className="text-sm text-white w-12 text-right">
                      {hasValue ? `${value.toFixed(1)}%` : '--'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Fetch grades on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchGrades();
    }
  }, [isAuthenticated]);

  const stats = calculateClassStats(selectedClass);

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold">Progress Analytics</h1>
            <p className="text-gray-400">Track your academic performance over time</p>
            <p className="text-sm text-gray-500 mt-2">
              Use the main School Dashboard to enter new grades
            </p>
          </div>

          {/* Class Selector */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex space-x-4">
              <button
                onClick={() => setSelectedClass('all')}
                className={`px-4 py-2 rounded ${selectedClass === 'all' ? 'bg-blue-600' : 'bg-[#2a2a2a] hover:bg-[#3a3a3a]'} transition-colors`}
              >
                All Classes
              </button>
              {classes.map(className => (
                <button
                  key={className}
                  onClick={() => setSelectedClass(className)}
                  className={`px-4 py-2 rounded ${selectedClass === className ? 'bg-blue-600' : 'bg-[#2a2a2a] hover:bg-[#3a3a3a]'} transition-colors text-sm`}
                >
                  {className.split(' ')[0]}
                </button>
              ))}
            </div>

            <button
              onClick={handleExportCSV}
              className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 rounded font-medium transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 01-2-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          </div>



          {/* Statistics Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-600 rounded-lg">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 002 0V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-400">Formative Avg</p>
                    <p className="text-2xl font-bold text-blue-400">{stats.formativeAvg}%</p>
                    <p className="text-xs text-gray-500 mt-1">{stats.validFormativeEntries} valid entries</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-red-600 rounded-lg">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 002 0V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-400">Summative Avg</p>
                    <p className="text-2xl font-bold text-red-400">{stats.summativeAvg}%</p>
                    <p className="text-xs text-gray-500 mt-1">{stats.validSummativeEntries} valid entries</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-600 rounded-lg">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 002 0V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-400">Overall Avg</p>
                    <p className="text-2xl font-bold text-green-400">{stats.overallAvg}%</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-600 rounded-lg">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 8a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-400">Total Records</p>
                    <p className="text-2xl font-bold text-purple-400">{stats.totalEntries}</p>
                    <p className="text-xs text-gray-500 mt-1">All grade entries</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Charts */}
          {selectedClass !== 'all' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">Grade Trends - {selectedClass}</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-3 flex justify-end">
                  <a href="/admin/full-charts" className="text-blue-400 hover:text-blue-300 text-sm">View full-width interactive charts →</a>
                </div>
                <SimpleChart
                  data={generateChartData(selectedClass)}
                  title="Formative Grades"
                  color="formative"
                />
                <SimpleChart
                  data={generateChartData(selectedClass)}
                  title="Summative Grades"
                  color="summative"
                />
                <SimpleChart
                  data={generateChartData(selectedClass)}
                  title="Overall Grades"
                  color="overall"
                />
              </div>
            </div>
          )}

          {/* Recent Grades Table */}
          <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">
              Recent Grade Entries {selectedClass !== 'all' ? `- ${selectedClass}` : ''}
            </h2>
            {getClassGrades(selectedClass).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#4d4d4d]">
                      <th className="text-left py-3 px-4">Date</th>
                      <th className="text-left py-3 px-4">Class</th>
                      <th className="text-left py-3 px-4">Formative</th>
                      <th className="text-left py-3 px-4">Summative</th>
                      <th className="text-left py-3 px-4">Overall</th>
                      <th className="text-left py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getClassGrades(selectedClass).slice(0, 10).map(grade => (
                      <tr key={grade.id} className="border-b border-[#2a2a2a]">
                        <td className="py-3 px-4">{new Date(grade.date).toLocaleDateString()}</td>
                        <td className="py-3 px-4">{grade.className}</td>
                        <td className="py-3 px-4">
                          <span className="text-blue-400">{grade.formativePercent?.toFixed(1)}%</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-red-400">{grade.summativePercent?.toFixed(1)}%</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-green-400 font-bold">{grade.overallPercent?.toFixed(1)}%</span>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleDeleteGrade(grade.id)}
                            className="text-red-500 hover:text-red-400 text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                No grade entries found for {selectedClass === 'all' ? 'any class' : selectedClass}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProgressAnalytics;
